/* ============================================================
   91.WATCHES — product gallery, zoom, lightbox, accordions
   Self-contained custom element, one instance per section.
   ============================================================ */
if (!customElements.get('ninetyone-product')) {
  customElements.define(
    'ninetyone-product',
    class NinetyOneProduct extends HTMLElement {
      connectedCallback() {
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.fine = window.matchMedia('(hover: hover) and (pointer: fine)');

        this.stage = this.querySelector('[data-np-stage]');
        this.stageImg = this.querySelector('[data-np-stage-img]');
        this.stageZoom = this.querySelector('[data-np-stage-zoom]');
        this.thumbs = Array.from(this.querySelectorAll('[data-np-thumb]'));
        this.swipe = this.querySelector('[data-np-swipe]');
        this.slides = Array.from(this.querySelectorAll('[data-np-slide]'));
        this.counter = this.querySelector('[data-np-count]');

        this.media = JSON.parse(this.querySelector('[data-np-media]')?.textContent || '[]');
        this.index = 0;

        this.initGallery();
        this.initLightbox();
        this.initAccordions();
        this.initVariants();
      }

      /* ── gallery ──────────────────────────────────────── */
      initGallery() {
        if (!this.media.length) return;

        this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.select(i)));
        this.slides.forEach((s, i) => s.addEventListener('click', () => this.openLb(i)));

        if (this.swipe) {
          let tick;
          this.swipe.addEventListener(
            'scroll',
            () => {
              clearTimeout(tick);
              tick = setTimeout(() => {
                const first = this.swipe.querySelector('[data-np-slide]');
                if (!first) return;
                const w = first.getBoundingClientRect().width + 8;
                this.setCount(Math.min(this.media.length - 1, Math.round(this.swipe.scrollLeft / w)));
              }, 60);
            },
            { passive: true }
          );
        }

        if (this.stage) {
          const zoomOn = this.stage.dataset.zoom === 'true';
          if (zoomOn) {
            this.stage.addEventListener('mouseenter', () => {
              if (this.fine.matches) this.stage.classList.add('is-zooming');
            });
            this.stage.addEventListener('mouseleave', () => {
              this.stage.classList.remove('is-zooming');
              this.stageZoom.style.transformOrigin = 'center center';
            });
            this.stage.addEventListener('mousemove', (e) => {
              if (!this.fine.matches) return;
              const r = this.stage.getBoundingClientRect();
              this.stageZoom.style.transformOrigin =
                ((e.clientX - r.left) / r.width) * 100 + '% ' + ((e.clientY - r.top) / r.height) * 100 + '%';
            });
          }
          if (this.lb) {
            this.stage.addEventListener('click', (e) => {
              if (e.target.closest('[data-np-expand]')) return;
              this.openLb(this.index);
            });
          }
        }

        this.querySelector('[data-np-expand]')?.addEventListener('click', (e) => {
          e.stopPropagation();
          this.openLb(this.index);
        });

        this.paint();
      }

      paint() {
        const m = this.media[this.index];
        if (!m || !this.stageImg) return;
        this.stageImg.src = m.src;
        this.stageImg.srcset = m.srcset || '';
        this.stageImg.alt = m.alt || '';
        if (this.stageZoom) this.stageZoom.src = m.zoom;
        this.thumbs.forEach((t, i) => t.setAttribute('aria-selected', i === this.index ? 'true' : 'false'));
      }

      select(i) {
        if (i === this.index || !this.media[i]) return;
        this.index = i;
        if (this.reduced || !this.stage) return this.paint();
        this.stage.classList.add('is-fading');
        setTimeout(() => {
          this.paint();
          requestAnimationFrame(() => this.stage.classList.remove('is-fading'));
        }, 160);
      }

      setCount(i) {
        if (!this.counter) return;
        const p = (n) => String(n + 1).padStart(2, '0');
        this.counter.textContent = p(i) + ' / ' + p(this.media.length - 1);
      }

      /* ── lightbox ─────────────────────────────────────── */
      initLightbox() {
        this.lb = this.querySelector('[data-np-lb]');
        if (!this.lb) return;

        this.lbStage = this.querySelector('[data-np-lb-stage]');
        this.lbImg = this.querySelector('[data-np-lb-img]');
        this.lbCount = this.querySelector('[data-np-lb-count]');
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
        this.MIN = 1;
        this.MAX = 5;

        this.querySelector('[data-np-lb-close]').addEventListener('click', () => this.closeLb());
        this.querySelector('[data-np-lb-in]').addEventListener('click', () => this.zoomBy(1.5));
        this.querySelector('[data-np-lb-out]').addEventListener('click', () => this.zoomBy(1 / 1.5));
        this.querySelector('[data-np-lb-prev]').addEventListener('click', () => this.showLb(this.lbIndex - 1));
        this.querySelector('[data-np-lb-next]').addEventListener('click', () => this.showLb(this.lbIndex + 1));

        this.lbStage.addEventListener(
          'wheel',
          (e) => {
            e.preventDefault();
            this.zoomBy(e.deltaY < 0 ? 1.12 : 1 / 1.12, e.clientX, e.clientY);
          },
          { passive: false }
        );
        this.lbStage.addEventListener('dblclick', (e) =>
          this.scale > 1 ? this.resetLb() : this.zoomBy(2.6, e.clientX, e.clientY)
        );

        const pts = new Map();
        let startDist = 0;
        let startScale = 1;
        let panFrom = null;

        this.lbStage.addEventListener('pointerdown', (e) => {
          this.lbStage.setPointerCapture(e.pointerId);
          pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
          if (pts.size === 1) {
            panFrom = { x: e.clientX - this.tx, y: e.clientY - this.ty };
            this.lbStage.classList.add('is-grabbing');
          } else if (pts.size === 2) {
            const [a, b] = Array.from(pts.values());
            startDist = Math.hypot(a.x - b.x, a.y - b.y);
            startScale = this.scale;
            panFrom = null;
          }
        });

        this.lbStage.addEventListener('pointermove', (e) => {
          if (!pts.has(e.pointerId)) return;
          pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
          if (pts.size === 2) {
            const [a, b] = Array.from(pts.values());
            const d = Math.hypot(a.x - b.x, a.y - b.y);
            if (startDist) {
              this.scale = Math.max(this.MIN, Math.min(this.MAX, startScale * (d / startDist)));
              if (this.scale === this.MIN) { this.tx = 0; this.ty = 0; }
              this.clampPan();
              this.applyLb();
            }
          } else if (panFrom && this.scale > 1) {
            this.tx = e.clientX - panFrom.x;
            this.ty = e.clientY - panFrom.y;
            this.clampPan();
            this.applyLb();
          }
        });

        const end = (e) => {
          pts.delete(e.pointerId);
          if (pts.size < 2) startDist = 0;
          if (pts.size === 0) { panFrom = null; this.lbStage.classList.remove('is-grabbing'); }
        };
        this.lbStage.addEventListener('pointerup', end);
        this.lbStage.addEventListener('pointercancel', end);

        this.onKey = (e) => {
          if (this.lb.hidden) return;
          if (e.key === 'Escape') this.closeLb();
          if (e.key === 'ArrowLeft') this.showLb(this.lbIndex - 1);
          if (e.key === 'ArrowRight') this.showLb(this.lbIndex + 1);
          if (e.key === '+' || e.key === '=') this.zoomBy(1.4);
          if (e.key === '-') this.zoomBy(1 / 1.4);
          if (e.key === 'Tab') {
            const f = Array.from(this.lb.querySelectorAll('button')).filter((b) => b.offsetParent !== null);
            if (!f.length) return;
            const first = f[0];
            const last = f[f.length - 1];
            if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
            else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
          }
        };
        document.addEventListener('keydown', this.onKey);
      }

      applyLb() {
        this.lbImg.style.transform = 'translate(' + this.tx + 'px,' + this.ty + 'px) scale(' + this.scale + ')';
      }
      resetLb() { this.scale = 1; this.tx = 0; this.ty = 0; this.applyLb(); }

      /* object-fit:contain paints smaller than the box — clamp against the paint */
      painted() {
        const r = this.lbStage.getBoundingClientRect();
        const cs = getComputedStyle(this.lbImg);
        const bw = this.lbImg.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
        const bh = this.lbImg.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
        const nw = this.lbImg.naturalWidth || 4;
        const nh = this.lbImg.naturalHeight || 5;
        const fit = Math.min(bw / nw, bh / nh);
        return { w: nw * fit, h: nh * fit, vw: r.width, vh: r.height };
      }

      clampPan() {
        const p = this.painted();
        const mx = Math.max(0, (p.w * this.scale - p.vw) / 2);
        const my = Math.max(0, (p.h * this.scale - p.vh) / 2);
        this.tx = Math.max(-mx, Math.min(mx, this.tx));
        this.ty = Math.max(-my, Math.min(my, this.ty));
      }

      zoomBy(f, ox, oy) {
        const prev = this.scale;
        this.scale = Math.max(this.MIN, Math.min(this.MAX, this.scale * f));
        if (this.scale === prev) return;
        if (ox !== undefined) {
          const r = this.lbStage.getBoundingClientRect();
          const dx = ox - r.left - r.width / 2;
          const dy = oy - r.top - r.height / 2;
          this.tx = dx - (dx - this.tx) * (this.scale / prev);
          this.ty = dy - (dy - this.ty) * (this.scale / prev);
        }
        if (this.scale === this.MIN) { this.tx = 0; this.ty = 0; }
        this.clampPan();
        this.applyLb();
      }

      showLb(i) {
        this.lbIndex = (i + this.media.length) % this.media.length;
        const m = this.media[this.lbIndex];
        this.lbImg.src = m.zoom;
        this.lbImg.alt = m.alt || '';
        const p = (n) => String(n + 1).padStart(2, '0');
        this.lbCount.textContent = p(this.lbIndex) + ' / ' + p(this.media.length - 1);
        this.resetLb();
      }

      openLb(i) {
        if (!this.lb) return;
        this.lastFocus = document.activeElement;
        this.lb.hidden = false;
        document.body.classList.add('np-lb-open');
        this.showLb(i);
        this.querySelector('[data-np-lb-close]').focus();
      }

      closeLb() {
        this.lb.hidden = true;
        document.body.classList.remove('np-lb-open');
        this.select(this.lbIndex);
        if (this.swipe) {
          const first = this.swipe.querySelector('[data-np-slide]');
          if (first) {
            const w = first.getBoundingClientRect().width + 8;
            this.swipe.scrollTo({ left: w * this.lbIndex, behavior: this.reduced ? 'auto' : 'smooth' });
          }
        }
        this.lastFocus?.focus();
      }

      /* ── accordions ───────────────────────────────────── */
      initAccordions() {
        this.querySelectorAll('[data-np-acc-btn]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const open = btn.getAttribute('aria-expanded') === 'true';
            btn.setAttribute('aria-expanded', String(!open));
            const panel = this.querySelector('#' + btn.getAttribute('aria-controls'));
            if (panel) panel.dataset.open = String(!open);
          });
        });
      }

      /* ── variants ─────────────────────────────────────── */
      initVariants() {
        const inputs = Array.from(this.querySelectorAll('[data-np-option]'));
        if (!inputs.length) return;

        this.variants = JSON.parse(this.querySelector('[data-np-variants]')?.textContent || '[]');
        inputs.forEach((el) =>
          el.addEventListener('change', () => {
            const label = el.closest('[data-np-opt-group]')?.querySelector('[data-np-opt-value]');
            if (label) label.textContent = el.value;
            this.onVariantChange();
          })
        );
        this.onVariantChange(true);
      }

      onVariantChange(initial) {
        if (!this.variants || !this.variants.length) return;

        const chosen = Array.from(this.querySelectorAll('[data-np-option]:checked')).map((i) => i.value);
        const variant = this.variants.find((v) => v.options.every((o, i) => o === chosen[i]));

        const priceEl = this.querySelector('[data-np-price]');
        if (priceEl && variant && variant.price_html) priceEl.innerHTML = variant.price_html;

        // Keep the Shopify product form synchronized with the custom picker.
        // Without this, Add to cart would continue submitting the first variant.
        const variantInput = this.querySelector('[data-np-variant-id]');
        const addButton = this.querySelector('[data-np-add-button]');
        const available = Boolean(variant && variant.available);

        if (variantInput) {
          if (variant) variantInput.value = variant.id;
          variantInput.disabled = !available;
          if (variant) variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (addButton) {
          addButton.disabled = !available;
          if (available) addButton.removeAttribute('aria-disabled');
          else addButton.setAttribute('aria-disabled', 'true');

          const label = addButton.querySelector('[data-np-add-label]');
          if (label) label.textContent = available ? addButton.dataset.addText : addButton.dataset.soldText;
        }

        this.querySelectorAll('[data-np-buy-now]').forEach((buyNow) => {
          buyNow.hidden = !available;

          buyNow.querySelectorAll('button').forEach((button) => {
            button.disabled = !available;
            if (available) button.removeAttribute('aria-disabled');
            else button.setAttribute('aria-disabled', 'true');
          });
        });

        this.querySelectorAll('[data-np-preorder-note]').forEach((note) => {
          note.hidden = !available;
        });

        // jump the gallery to the variant's own image
        if (!initial && variant && variant.media_index != null) this.select(variant.media_index);

        if (!initial && variant) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
      }

      disconnectedCallback() {
        if (this.onKey) document.removeEventListener('keydown', this.onKey);
      }
    }
  );
}
